import React, { useState, useEffect } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { postAnswer } from "../../api/answer";
import { getQuizById, getQuizIdsByScenario } from "../../api/quiz";
import { useGameTexts } from "../../hooks/useGameTexts";
import handGood from "../../assets/hand-good.png";
import handWrong from "../../assets/hand-wrong.png";
import iconFalseCorrect from "../../assets/icon-false-correct.png";
import iconFalseSelected from "../../assets/icon-false-selected.png";
import iconFalseWrong from "../../assets/icon-false-wrong.png";
import iconFalse from "../../assets/icon-false.png";
import iconTrueCorrect from "../../assets/icon-true-correct.png";
import iconTrueSelected from "../../assets/icon-true-selected.png";
import iconTrueWrong from "../../assets/icon-true-wrong.png";
import iconTrue from "../../assets/icon-true.png";
import hand from "../../assets/mano.png";
import ScenarioBar from "../../components/ScenarioBar";
import { useTimer } from "../../hooks/useTimer";
import { updateScore, updateTime } from "../../redux/actions";
import { useLang } from "../../translations/i18n";
import { QUIZZES, ROUTES, SCENARIOS, SCORE_TYPES } from "../../utils/constants";
import {
	getScenarioByScreen,
	getTimer,
	updateStats,
} from "../../utils/helpers";
import {
	alertError,
	modalGenericSmall,
	modalQuestionInfo,
} from "../../utils/logger";


function buildQuestionInfoHtml(enunciado) {
	return `<div class="d-flex flex-column align-items-start justify-content-center txt16">
		<div class="txt-turquoise bold txt20 text-center">
		${enunciado}
		</div>
		<div class="w-100 px-5">		
		<div class="d-flex align-items-center justify-content-between mt-5">
			<div class="">Identificación de la infiltrada</div>
			<div class="bg-correct p-2" style="width: 150px;">
				<img width="25" src=${handGood} class="me-1" />
				<span class="bold">+${10}</span> puntos
			</div>
		</div>
		<div class="d-flex align-items-center justify-content-between mt-3">
			<div class="w-50">Identificación de la infiltrada al primer intento</div>
			<div class="bg-correct p-2" style="width: 150px;">
				<img width="25" src=${handGood} class="me-2" />
				<span class="bold">+${20}</span> puntos
			</div>
		</div>
		<div class="d-flex align-items-center justify-content-between mt-3">
			<div class="w-50">Cada identificación incorrecta de la infiltrada</div>
			<div class="bg-incorrect p-2" style="width: 150px;">
				<img width="18" src=${handWrong} class="ms-1 me-2" />
				<span class="bold">${-10}</span> puntos
			</div>
		</div>
		</div>
	</div>`;
}

const QUIZ_ID = QUIZZES.SCENARIO5_2;
const SCENARIO_ID = SCENARIOS.SCENARIO_5;
const QUIZ_CACHE_KEY = `${SCENARIO_ID}-1`;
const NAVIGATE_TO = ROUTES.SCENARIO5_PART2_QUESTION2;

const CORRECT_ANSWER_POINTS = 40;

function shuffleArray(arr) {
	const a = [...arr];
	for (let i = a.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[a[i], a[j]] = [a[j], a[i]];
	}
	return a;
}

function Scenario5Part2({ intl }) {
	const lang = useLang();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const participant = useSelector((state) => state.group);
	const cachedQuiz = useSelector((state) => state.quizCache?.[QUIZ_CACHE_KEY]);
	const { texts, ready: textsReady } = useGameTexts(participant?.game);

	const scenarioNum = getScenarioByScreen(location.pathname);

	const instruction = texts["scenario5.2.instruction"] ?? "";
	const citation = texts["scenario5.2.citation"] ?? "";
	const footer = texts["scenario5.2.footer"] ?? "";
	const questionInfoEnunciado = texts["scenario5.2.questionInfo.enunciado"] ?? "";

	const [points, setPoints] = useState({ score: 0 });
	const [time, setTime] = useState(0);
	const [intentos, setIntentos] = useState(1);
	const [isCompleted, setIsCompleted] = useState(false);
	const [options, setOptions] = useState([]);
	const [resolvedQuizId, setResolvedQuizId] = useState(null);
	const [quizLoading, setQuizLoading] = useState(true);

	useEffect(() => {
		const gameId = participant?.game;
		if (!gameId) {
			setQuizLoading(false);
			return;
		}
		if (cachedQuiz?.data?.options?.length) {
			setResolvedQuizId(cachedQuiz.resolvedQuizId || null);
			const mapped = cachedQuiz.data.options.map((o) => ({
				id: o.id,
				text: o.text || "",
				explanation: o.explanation || "",
				selected: null,
				correct: Boolean(o.correct),
			}));
			setOptions(shuffleArray(mapped));
			setQuizLoading(false);
			return;
		}
		const params = { game: gameId };
		getQuizIdsByScenario(SCENARIO_ID, params)
			.then((listRes) => {
				const list = listRes?.data && Array.isArray(listRes.data) ? listRes.data : [];
				const id = list[1]?._id || QUIZ_ID;
				setResolvedQuizId(id);
				return getQuizById(id);
			})
			.then((res) => {
				if (res?.status === 200 && res.data?.options?.length) {
					const mapped = res.data.options.map((o) => ({
						id: o.id,
						text: o.text || "",
						explanation: o.explanation || "",
						selected: null,
						correct: Boolean(o.correct),
					}));
					setOptions(shuffleArray(mapped));
				}
			})
			.catch(() => {})
			.finally(() => setQuizLoading(false));
	}, [participant?.game, cachedQuiz]);

	useTimer(
		() => {
			setTime(time + 1);
		},
		isCompleted ? null : 1000
	);

	const SelectOption = ({ id, number }) => {
		let option = options.find((x) => x.id === id);

		function handleOptionClick(newState) {
			if (isCompleted) return;

			let updatedOptions = options.map((x) =>
				x.id === id ? { ...x, selected: newState } : { ...x }
			);

			setOptions(updatedOptions);
		}

		function getTrueIcon() {
			if (isCompleted) {
				if (option?.selected === option?.correct && option?.correct)
					return iconTrueCorrect;
				else if (
					option?.selected !== option?.correct &&
					option?.correct
				)
					return iconTrueWrong;
				else return iconTrue;
			} else if (option?.selected) return iconTrueSelected;
			else return iconTrue;
		}

		function getFalseIcon() {
			if (isCompleted) {
				if (
					option?.selected === option?.correct &&
					option?.correct === false
				)
					return iconFalseCorrect;
				else if (
					option?.selected !== option?.correct &&
					option?.correct === false
				)
					return iconFalseWrong;
				else return iconFalse;
			} else if (option?.selected === false) return iconFalseSelected;
			else return iconFalse;
		}

		return (
			<div className="d-flex align-items-center justify-content-center mt-3">
				<div
					className="d-flex align-items-center justify-content-center txt-dark-green p-2"
					style={{
						position: "relative",
						minHeight: "10vh",
						height: "fit-content",
						borderRadius: "10px",
						backgroundColor: "#ffffff",
					}}
				>
					<div className="d-flex align-items-start">
						<div>{number}.</div>
						<div className="ms-2">
							<span dangerouslySetInnerHTML={{ __html: option?.text || "" }} />
							{isCompleted && (
								<div className="bold" dangerouslySetInnerHTML={{ __html: option?.explanation || "" }} />
							)}
						</div>
					</div>
				</div>
				<img
					className="ms-3"
					onClick={() => handleOptionClick(true)}
					src={getTrueIcon()}
				/>
				<img
					className="ms-2"
					onClick={() => handleOptionClick(false)}
					src={getFalseIcon()}
				/>
			</div>
		);
	};

	async function submitAnswer({ correct }) {
		setIsCompleted(true);
		const score = correct && intentos <= 3 ? CORRECT_ANSWER_POINTS : 0;
		let totalTime = getTimer();
		dispatch(updateTime(totalTime, 1));
		postAnswer({
			scenario: SCENARIO_ID,
			quiz: resolvedQuizId ?? QUIZ_ID,
			group: participant?._id,
			score: score,
			time: time,
			correct: correct,
		})
			.then((res) => {
				if (res.status === 201) {
					dispatch(updateScore(score, SCORE_TYPES.SCORE));

					updateStats({
						...participant,
						time: totalTime,
						score: participant?.score + score,
						lastVisited: NAVIGATE_TO,
					});
				}
			})
			.catch((error) => {});
	}

	function checkAnswers() {
		if (options?.some((x) => x?.selected === null)) {
			alertError({
				error: "Debes responder todas las afirmaciones para poder continuar.",
			});
			return;
		}
		const wrongAnswers = options?.filter((x) => x?.selected !== x?.correct);
		const correct = wrongAnswers?.length === 0;

		const text = `<div class="text-center txt-black">
		<div class="txt-turquoise bold">
			${
				correct && intentos < 3
					? "¡Has acertado todas las afirmaciones!"
					: `Has acertado ${
							options?.length - wrongAnswers?.length
					  } afirmaciones.`
			} 
		</div>
		<div class="d-flex justify-content-center my-3">
			${
				correct && intentos < 3
					? `<div class="bg-correct txt-black w-fit-content p-2 bold">
					<img width="25" src=${handGood} class="me-1" />
					+${CORRECT_ANSWER_POINTS} puntos
				</div>`
					: ""
			}
		</div>
		<div>
			${
				correct && intentos < 3
					? "Pulsa el botón AVANZAR para ver la justificación"
					: intentos < 3
					? `Inténtalo de nuevo.\nTe quedan ${
							3 - intentos
					  } intento(s) para poder ganar los ${CORRECT_ANSWER_POINTS} puntos.`
					: "Se han acabado los intentos.\nPulsa el botón AVANZAR para ver la justificación"
			}
		</div>
	</div>`;

		setIntentos(intentos + 1);

		modalGenericSmall({ html: text }).then((res) => {
			if (res.isConfirmed) {
				if (correct || intentos === 3)
					submitAnswer({ correct: correct });
			}
		});
	}

	return (
		<div className="h-100 bg-prueba">
			<div className="container">
				<div className="row h-100">
					{!isCompleted && (
						<div className="d-flex justify-content-start align-items-center">
							<img className="me-2" src={hand} />
							<div className="txt-white">
								{!textsReady ? "Cargando..." : <span dangerouslySetInnerHTML={{ __html: instruction }} />}
							</div>
						</div>
					)}
					<div className="row d-flex justify-content-center align-items-start mt-2">
						<div className="col-2">
							<div
								className="txt8 txt-white"
								style={{ lineHeight: "15px" }}
								dangerouslySetInnerHTML={{ __html: citation }}
							/>
						</div>
						<div className="col-10 pt-2">
							{quizLoading ? (
								<div className="txt-white">Cargando opciones...</div>
							) : (
								options.map((option, index) => (
									<div key={option.id}>
										<SelectOption id={option.id} number={index + 1} />
									</div>
								))
							)}
						</div>
						<div
							className="txtWhite d-flex justify-content-center mt-3 txt12"
							dangerouslySetInnerHTML={{ __html: footer }}
						/>
					</div>
				</div>
			</div>
			<div
				className="container"
				style={{
					position: "absolute",
					right: "0",
					bottom: "13vh",
					zIndex: 1000,
				}}
			>
				<div className="d-flex justify-content-end">
					{quizLoading ? null : !isCompleted ? (
						<>
							<div
								className="btn-main"
								onClick={() => checkAnswers()}
							>
								<div className="text-btn-main">AVANZAR</div>
							</div>
						</>
					) : (
						<div
							className="btn-main"
							onClick={() =>
								modalQuestionInfo({ html: buildQuestionInfoHtml(questionInfoEnunciado) }).then(
									(res) => {
										if (res.isConfirmed)
											navigate(
												ROUTES.SCENARIO5_PART2_QUESTION2
											);
									}
								)
							}
						>
							<div className="text-btn-main">AVANZAR</div>
						</div>
					)}
				</div>
			</div>
			<ScenarioBar activeScenario={scenarioNum}></ScenarioBar>
		</div>
	);
}

export default injectIntl(Scenario5Part2);
